import { Component, OnInit, inject } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { of, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  ActiveCampaign,
  campaignProductDescription,
  campaignVariationDescription,
  isEnablingSuccess,
  variationsForCampaign,
} from '../../core/campaign/campaign-contract';
import { CampaignReadOutcome, CampaignReadService } from '../../core/campaign/campaign-read.service';
import { SimplifiedProductMatchService } from '../../core/campaign/simplified-product-match';
import { ProductV2, ProductV2Variation } from '../../core/models/product-v2.model';
import {
  isRegistryDomainProductUuid,
  isRegistryDomainVariationUuid,
} from '../../core/utils/cart-domain-suggestion-rules';
import { ProductPageProductLoadService } from '../../core/services/product-page-product-load.service';
import { clearCampaignLoginFlowTypes, writeCampaignLoginFlowTypes } from '../../core/campaign/campaign-checkout-flag';
import { CampaignCartResetService } from '../../core/campaign/campaign-cart-reset.service';
import { CampaignPurchaseService } from '../../core/campaign/campaign-purchase.service';
import { I18nService } from '../../core/services/i18n.service';
import { ToastService } from '../../shared/services/toast.service';
import {
  clearStoredExternalChannelCampaign,
  persistExternalChannelCampaign,
  readStoredExternalChannelCampaign,
} from '../../core/utils/external-channel-access.util';
import { sanitizeSlug } from '../../core/utils/utils';
import { CarouselComponent } from '../../shared/components/carousel/carousel.component';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { ImageFallbackPipe } from '../../shared/pipes/image-fallback.pipe';

@Component({
  selector: 'umkt-campaign-landing',
  standalone: true,
  imports: [CarouselComponent, ErrorDisplayComponent, ProductCardComponent, I18nPipe, ImageFallbackPipe],
  templateUrl: './campaign-landing.component.html',
  styleUrl: './campaign-landing.component.scss',
})
export class CampaignLandingComponent implements OnInit {
  product: ProductV2 | null = null;
  variations: ProductV2Variation[] = [];
  sanitizedDescription: SafeHtml | string = '';
  loadError = false;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);
  private readonly match = inject(SimplifiedProductMatchService);
  private readonly productLoad = inject(ProductPageProductLoadService);
  private readonly campaignRead = inject(CampaignReadService);
  private readonly purchase = inject(CampaignPurchaseService);
  private readonly cartReset = inject(CampaignCartResetService);

  private outcome: CampaignReadOutcome | null = null;
  private productReady = false;
  private persisted = false;
  private leftHome = false;

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('productSlug') ?? '';
    const queryCode = (this.route.snapshot.queryParamMap.get('campaign') ?? '').trim();
    const code = queryCode || readStoredExternalChannelCampaign();
    const outcome$ = code ? this.campaignRead.read(code).pipe(take(1)) : of(null);

    forkJoin({
      matches: this.match.matchAll(slug).pipe(take(1)),
      outcome: outcome$,
    }).subscribe(({ matches, outcome }) => {
      if (outcome?.kind === 'notFound') {
        this.leaveForHome('shared.messages.campaignMissing');
        return;
      }

      this.outcome = outcome;
      const chosen = this.productForCampaign(matches, outcome);
      if (outcome?.kind === 'active' && matches.length > 0 && !chosen) {
        this.productReady = true;
        this.leaveForHome('shared.messages.campaignProductOutside');
        return;
      }

      if (!chosen?.uuid) {
        this.productReady = true;
        this.applyEnabling();
        return;
      }

      const category = chosen.categories?.[0]?.name ?? '';
      this.productLoad.loadSvaByUuid(chosen.uuid, category).subscribe({
        next: result => {
          this.product = result?.product ?? null;
          this.sanitizedDescription = result?.sanitizedDescription ?? this.product?.description ?? '';
          this.productReady = true;
          this.applyEnabling();
        },
        error: () => {
          this.loadError = true;
          this.productReady = true;
        },
      });
    });
  }

  private productForCampaign(
    matches: ProductV2[],
    outcome: CampaignReadOutcome | null
  ): ProductV2 | null {
    if (outcome?.kind === 'active') {
      return matches.find(product => isEnablingSuccess(outcome.campaign, product.uuid)) ?? null;
    }
    return matches[0] ?? null;
  }

  private leaveForHome(messageKey: string): void {
    if (this.leftHome) {
      return;
    }

    this.leftHome = true;
    clearStoredExternalChannelCampaign();
    clearCampaignLoginFlowTypes();
    this.toast.showError(this.i18n.instant(messageKey));
    this.router.navigate(['/'], { replaceUrl: true });
  }

  private applyEnabling(): void {
    if (this.leftHome || !this.productReady || !this.product?.uuid) {
      this.variations = [];
      return;
    }

    if (this.outcome?.kind === 'active' && !isEnablingSuccess(this.outcome.campaign, this.product.uuid)) {
      this.leaveForHome('shared.messages.campaignProductOutside');
      return;
    }

    const campaign = this.enablingCampaign();
    if (!campaign) {
      this.variations = [];
      return;
    }

    this.variations = variationsForCampaign(this.product, campaign);
    const description = campaignProductDescription(campaign, this.product.uuid);
    if (description) {
      this.sanitizedDescription = escapeCampaignText(description);
    }
    if (this.persisted) {
      return;
    }

    this.persisted = true;
    persistExternalChannelCampaign(campaign.code);
    writeCampaignLoginFlowTypes(campaign.flowTypes);
    this.cartReset.reset(campaign.code);
  }

  buy(variation: ProductV2Variation, quantity = 1): void {
    const campaign = this.enablingCampaign();
    if (!this.product || !campaign) {
      return;
    }

    this.purchase.buy(this.product, variation, Math.max(1, quantity || 1), campaign.flowTypes);
  }

  openProductPage(): void {
    if (!this.product?.name || !this.categoryName) {
      return;
    }

    const queryParams: Record<string, string> = {};
    for (const key of this.route.snapshot.queryParamMap.keys) {
      const value = this.route.snapshot.queryParamMap.get(key);
      if (value) {
        queryParams[key] = value;
      }
    }

    this.router.navigate([sanitizeSlug(this.categoryName), sanitizeSlug(this.product.name)], {
      queryParams,
    });
  }

  private enablingCampaign(): ActiveCampaign | null {
    if (this.outcome?.kind !== 'active' || !this.product?.uuid) {
      return null;
    }
    if (!isEnablingSuccess(this.outcome.campaign, this.product.uuid)) {
      return null;
    }
    return this.outcome.campaign;
  }

  get categoryName(): string {
    return this.product?.categories?.[0]?.name ?? '';
  }

  get fromPrice(): { reais: string; cents: string } | null {
    if (!this.variations.length) {
      return null;
    }
    const amount = Math.min(...this.variations.map(variation => this.offerPrice(variation)));
    return this.splitPrice(amount);
  }

  variationText(variation: ProductV2Variation): string {
    const campaign = this.enablingCampaign();
    const override = campaign && this.product?.uuid
      ? campaignVariationDescription(campaign, this.product.uuid, variation.uuid)
      : null;
    return override ?? (variation.excerpt || variation.description || '');
  }

  offerPrice(variation: ProductV2Variation): number {
    const amount = variation.price ?? 0;
    if (variation.paymentRecurrence !== 'annual' || this.keepsAnnualPrice(variation)) {
      return amount;
    }
    return amount / 12;
  }

  private keepsAnnualPrice(variation: ProductV2Variation): boolean {
    return isRegistryDomainVariationUuid(variation.uuid) || isRegistryDomainProductUuid(this.product?.uuid);
  }

  isRecommended(variation: ProductV2Variation): boolean {
    const amount = this.offerPrice(variation);
    return this.variations.every(other => this.offerPrice(other) <= amount);
  }

  scrollToOffers(): void {
    document.getElementById('campaign-offers')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private splitPrice(amount: number): { reais: string; cents: string } {
    const [reais, cents] = amount.toFixed(2).split('.');
    return {
      reais: Number(reais).toLocaleString('pt-BR'),
      cents,
    };
  }

}

function escapeCampaignText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
